import { BillIcon, ChevronLeftIcon } from '../../../assets/Icons';
import type { Charges, Details, Taxes } from '../../../types/codexOrder';
import { formatMoney } from '../../../helpers/utils';

const TIP_TITLE = 'Tip';

function isPositive(entry: { value?: number }) {
  return typeof entry.value === 'number' && entry.value > 0;
}

function OrderSummary({
  details,
  onTipClick,
}: {
  details: Details;
  onTipClick?: () => void;
}) {
  if (!details) {
    return null;
  }

  const charges: Charges[] = (details.charges ?? []).filter(isPositive);
  const taxes: Taxes[] = (details.taxes ?? []).filter(isPositive);

  // Older orders predate the per-id breakdown payload, so fall back to the
  // combined 'Taxes and charges' + separate 'Delivery fee' layout to keep
  // those receipts looking the same as before.
  const showFallback = charges.length === 0 && taxes.length === 0;
  const fallbackDeliveryFee = details.delivery_charge ?? 0;
  const fallbackTaxesAndCharges =
    (details.total_taxes ?? 0) +
    (details.total_charges ?? 0) -
    fallbackDeliveryFee;

  return (
    <div className="p-4 rounded-lg bg-white mb-4">
      <div className="flex items-center gap-x-2 mb-3">
        <BillIcon className="text-brand-icon" />
        <p className="font-bold">Bill Summary</p>
      </div>
      <div className="flex flex-col gap-y-4 text-sm text-up-black-50">
        <div className="flex items-center justify-between gap-x-2">
          <p>Item subtotal</p>
          <p>{formatMoney(details.order_subtotal)}</p>
        </div>
        {charges.map((charge, index) => {
          const isTip = charge.title === TIP_TITLE && !!onTipClick;
          return (
            <div
              key={'charge-' + String(index) + '-' + charge.title}
              onClick={isTip ? onTipClick : undefined}
              className={
                'flex items-center justify-between gap-x-2' +
                (isTip ? ' text-brand-icon cursor-pointer' : '')
              }>
              <p className="flex items-center gap-x-1">
                {charge.title}
                {isTip && <ChevronLeftIcon className="w-3 h-3 rotate-180" />}
              </p>
              <p>{formatMoney(charge.value)}</p>
            </div>
          );
        })}
        {taxes.map((tax, index) => (
          <div
            key={'tax-' + String(index) + '-' + tax.title}
            className="flex items-center justify-between gap-x-2">
            <p>{tax.title}</p>
            <p>{formatMoney(tax.value)}</p>
          </div>
        ))}
        {showFallback && !!fallbackTaxesAndCharges && (
          <div className="flex items-center justify-between gap-x-2">
            <p>Taxes and charges</p>
            <p>{formatMoney(fallbackTaxesAndCharges)}</p>
          </div>
        )}
        {showFallback && !!fallbackDeliveryFee && (
          <div className="flex items-center justify-between gap-x-2">
            <p>Delivery fee</p>
            <p>{formatMoney(fallbackDeliveryFee)}</p>
          </div>
        )}
        <div className="border-b text-up-black-20" />
        <div className="flex items-center justify-between gap-x-2 text-base text-up-black-80">
          <p>Total</p>
          <p>{formatMoney(details.order_total)}</p>
        </div>
      </div>
    </div>
  );
}

export default OrderSummary;
